// src/Screens/Components/ApplicationStatus.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import styled from 'styled-components';
import logo from "../../Assets/logo.png";
import ProgressBar from './ProgressBar';
import { ButtonsCard } from './ButtonsCard';

const GradientBackground = styled.div`
  background: linear-gradient(0deg, rgba(133,212,213,1) 13%, rgba(207,244,149,1) 100%);
  background-size: 400% 400%;
  animation: gradient 15s ease infinite;
  
  @keyframes gradient {
    0% {
      background-position: 50% 0%;
    }
    50% {
      background-position: 50% 100%;
    }
    100% {
      background-position: 50% 0%;
    }
  }
`;

const ApplicationStatus = () => {
  const [status, setStatus] = useState('pending');
  const [applicationData, setApplicationData] = useState(null);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const fetchApplicationStatus = async () => {
      if (id && id !== 'null') {
        const docRef = doc(db, 'applications', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setStatus(docSnap.data().status);
          setApplicationData(docSnap.data());
        } else {
          console.error('No application found with ID:', id);
          // Optionally, redirect to an error page or back to the form
          // navigate('/start-application');
        }
      } else {
        console.error('Invalid application ID:', id);
        // Optionally, redirect to an error page or back to the form
        // navigate('/start-application');
      }
    };

    fetchApplicationStatus();
  }, [id, navigate]);

  const getProgressPercentage = () => {
    switch (status) {
      case 'pending':
        return 33;
      case 'under review':
        return 66;
      case 'decision made':
        return 100;
      default:
        return 0;
    }
  };

  const renderDocumentLink = (url: string | null, label: string) => {
    if (url) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 underline"
        >
          {label}
        </a>
      );
    }
    return 'Not uploaded';
  };

  const handleEditApplication = () => {
    // Navigate to the ApplicationForm with the current application data and ID
    navigate(`/edit-application/${id}`, { state: { applicationData, id } });
  };

  return (
    <GradientBackground className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              <img src={logo} alt="Company Logo" className="h-20 w-auto" />
            </button>
          </div>
          <h1 className="text-5xl font-bold font-philosopher sans-serif text-gray-900">Application Status</h1>
          <div className="w-24"></div> {/* Spacer for alignment */}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-grow p-8">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-8">
            <h2 className="text-3xl font-philosopher font-bold text-gray-900 mb-6">
              {applicationData ? "Application Successfully Submitted" : "Loading Application Status..."}
            </h2>
            {applicationData ? (
              <>
                <ProgressBar 
                  categories={['Submitted', 'Under Review', 'Decision Made']}
                  currentCategory={getProgressPercentage() / 33 - 1}
                />
                <p className="text-xl text-gray-700 mt-4 mb-8">Current Status: <span className="font-semibold capitalize">{status}</span></p>

                <h3 className="text-2xl font-philosopher font-semibold text-gray-900 mb-4">Review Your Information</h3>
                {/* Your application id is */}
                <h3 className="text-xl font-philosopher font-semibold text-gray-900 mb-4">Your application id is: {id}</h3>

                <div className="space-y-6">
                  <section>
                    <h4 className="text-xl font-philosopher font-semibold text-gray-900 mb-2">Basic Information</h4>
                    <p><strong>Name:</strong> {applicationData.firstName} {applicationData.lastName}</p>
                    <p><strong>Email:</strong> {applicationData.email}</p>
                    <p><strong>Phone:</strong> {applicationData.phoneNumber}</p>
                    <p><strong>Date of Birth:</strong> {applicationData.dateOfBirth}</p>
                    <p><strong>Marital Status:</strong> {applicationData.maritalStatus}</p>
                  </section>

                  <section>
                    <h4 className="text-xl font-philosopher font-semibold text-gray-900 mb-2">Loan Information</h4>
                    <p><strong>Loan Purpose:</strong> {applicationData.loanPurpose}</p>
                    <p><strong>Property Address:</strong> {applicationData.propertyAddress}</p>
                    <p><strong>Current Address:</strong> {applicationData.currentAddress}</p>
                    <p><strong>Years at Current Address:</strong> {applicationData.yearsAtAddress}</p>
                    <p><strong>Budget:</strong> {applicationData.budget}</p>
                    <p><strong>Credit Score:</strong> {applicationData.creditScore}</p>
                    <p><strong>Timeframe:</strong> {applicationData.timeframe}</p>
                    <p><strong>First Time Buyer:</strong> {applicationData.firstTimeBuyer ? 'Yes' : 'No'}</p>
                  </section>

                  <section>
                    <h4 className="text-xl font-philosopher font-semibold text-gray-900 mb-2">Financial Information</h4>
                    <p><strong>Annual Income:</strong> ${applicationData.annualIncome}</p>
                    <p><strong>Other Income:</strong> ${applicationData.otherIncome}</p>
                    <p><strong>Assets:</strong> {applicationData.assets}</p>
                    <p><strong>Liabilities:</strong> {applicationData.liabilities}</p>
                  </section>

                  <section>
                    <h4 className="text-xl font-philosopher font-semibold text-gray-900 mb-2">Employment Information</h4>
                    <p><strong>Employer Name:</strong> {applicationData.employerName}</p>
                    <p><strong>Position:</strong> {applicationData.position}</p>
                    <p><strong>Years Employed:</strong> {applicationData.yearsEmployed}</p>
                  </section>

                  <section>
                    <h4 className="text-xl font-philosopher font-semibold text-gray-900 mb-2">Loan Details</h4>
                    <p><strong>Loan Amount:</strong> ${applicationData.loanAmount}</p>
                    <p><strong>Down Payment:</strong> ${applicationData.downPayment}</p>
                  </section>

                  <section>
                    <h4 className="text-xl font-philosopher font-semibold text-gray-900 mb-2">Uploaded Documents</h4>
                    <p><strong>Pay Stubs:</strong> {renderDocumentLink(applicationData.payStubs, 'View Pay Stubs')}</p>
                    <p><strong>Social Security Card:</strong> {renderDocumentLink(applicationData.socialSecurityCard, 'View Social Security Card')}</p>
                    <p><strong>Driver License:</strong> {renderDocumentLink(applicationData.driverLicense, 'View Driver License')}</p>
                    <p><strong>Proof of Residency:</strong> {renderDocumentLink(applicationData.proofOfResidency, 'View Proof of Residency')}</p>
                  </section>
                </div>

                <div className="flex justify-between mt-8">
                  <ButtonsCard onClick={() => navigate('/')} className="w-auto h-auto">
                    <button className="p-[3px] relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg" />
                      <div className="px-8 py-2 bg-white rounded-[6px] relative group transition duration-200 text-black hover:bg-transparent hover:text-white">
                        Back to Home
                      </div>
                    </button>
                  </ButtonsCard>

                  <ButtonsCard onClick={handleEditApplication} className="w-auto h-auto">
                    <button className="p-[3px] relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg" />
                      <div className="px-8 py-2 bg-white rounded-[6px] relative group transition duration-200 text-black hover:bg-transparent hover:text-white">
                        Edit My Application
                      </div>
                    </button>
                  </ButtonsCard>
                </div>
              </>
            ) : (
              <p>Loading application data...</p>
            )}
          </div>
        </div>
      </main>
    </GradientBackground>
  );
};

export default ApplicationStatus;
